import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  useTheme,
  Divider,
  Typography,
  Chip
} from '@mui/material';
import { 
  Dashboard as DashboardIcon, 
  Event as EventIcon, 
  People as PeopleIcon,
  Campaign as CampaignIcon,
  AccountCircle as AccountCircleIcon,
  AdminPanelSettings as AdminIcon,
  Phone as CallerIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/supabase/AuthContext';
import { getUserRole, usePermissions } from '../lib/supabase/userService';
import { UserRole } from '../types/admin';

const drawerWidth = 240;

export const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [userRole, setUserRole] = useState<UserRole>('client');
  const [loading, setLoading] = useState(true);
  
  const permissions = usePermissions(userRole);

  // Charger le rôle utilisateur
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await getUserRole(user.id);
        if (data) {
          setUserRole(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du rôle:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  const baseMenuItems = [
    ...(userRole === 'client' ? [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      { text: 'RDV', icon: <EventIcon />, path: '/rdv' },
      { text: 'CRM', icon: <PeopleIcon />, path: '/crm' },
      { text: 'Mes Campagnes', icon: <CampaignIcon />, path: '/campagnes' },
    ] : []),
  ];

  const roleBasedMenuItems = [
    ...(permissions.canViewCallerDashboard && userRole === 'caller' ? [
      { text: 'Interface Caller', icon: <CallerIcon />, path: '/caller' }
    ] : []),
    ...(permissions.canViewAdminDashboard && userRole === 'admin' ? [
      { text: 'Administration', icon: <AdminIcon />, path: '/admin' }
    ] : []),
  ];

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'admin': return 'error';
      case 'caller': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'caller': return 'Caller';
      default: return 'Client';
    }
  };

  if (loading) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ mt: 8, p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Chargement...
          </Typography>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default,
          borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Menu principal */}
      <Box sx={{ flex: 1, overflow: 'auto', mt: 8 }}>
        <List>
          {baseMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Menu spécifique aux rôles */}
        {roleBasedMenuItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Outils avancés
              </Typography>
            </Box>
            <List>
              {roleBasedMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    selected={location.pathname === item.path}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>

      {/* Mon Compte en bas */}
      <Box sx={{ 
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        mt: 'auto',
      }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/mon-compte')}
              selected={location.pathname === '/mon-compte'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemIcon>
                <AccountCircleIcon />
              </ListItemIcon>
              <ListItemText primary="Mon Compte" sx={{ color: 'black' }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
}; 